import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  InputNumber,
  Modal,
  Table,
  Tag,
  Space,
  Typography,
  message,
  Tooltip,
  Statistic,
  Row,
  Col,
  Tabs,
  Empty,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DollarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import bountyService from '../services/bountyService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const BountySystem = () => {
  const { user, isAuthenticated } = useAuth();
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [bounties, setBounties] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [userBounties, setUserBounties] = useState({ created: [], completed: [] });
  
  // 创建悬赏模态框
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm, setCreateForm] = useState({
    doi: '',
    amount: 1000,
    title: '',
    description: '',
    txId: ''
  });
  const [createLoading, setCreateLoading] = useState(false);

  // 加载数据
  useEffect(() => {
    if (isAuthenticated) {
      loadBounties();
      loadStatistics();
      loadUserBounties();
    }
  }, [isAuthenticated]);

  const loadBounties = async () => {
    try {
      setLoading(true);
      const response = await bountyService.getBounties({ status: 'active', limit: 50 });
      setBounties(response.bounties || []);
    } catch (error) {
      message.error('Failed to load bounties');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await bountyService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.warn('Failed to load statistics:', error);
    }
  };

  const loadUserBounties = async () => {
    if (!user?.wallet_address) return;
    
    try {
      const [created, completed] = await Promise.all([
        bountyService.getUserBounties(user.wallet_address, 'created'),
        bountyService.getUserBounties(user.wallet_address, 'completed')
      ]);
      setUserBounties({ created: created.bounties || [], completed: completed.bounties || [] });
    } catch (error) {
      console.warn('Failed to load user bounties:', error);
    }
  };

  const handleCreateBounty = async () => {
    if (!createForm.doi || !createForm.txId || createForm.amount < 1000) {
      message.error('Please fill in all required fields');
      return;
    }

    try {
      setCreateLoading(true);
      await bountyService.createBounty({
        doi: createForm.doi,
        tx_id: createForm.txId,
        title: createForm.title,
        description: createForm.description
      });
      
      message.success('Bounty created successfully!');
      setCreateModalVisible(false);
      setCreateForm({ doi: '', amount: 1000, title: '', description: '', txId: '' });
      loadBounties();
      loadStatistics();
      loadUserBounties();
    } catch (error) {
      message.error(error.message || 'Failed to create bounty');
    } finally {
      setCreateLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat().format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const bountyColumns = [
    {
      title: 'DOI',
      dataIndex: 'doi',
      key: 'doi',
      render: (doi) => (
        <Text code style={{ fontSize: '12px' }}>{doi}</Text>
      ),
      width: 200,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => title || record.doi,
    },
    {
      title: 'Amount',
      dataIndex: 'scai_amount',
      key: 'amount',
      render: (amount) => (
        <Tag color="gold" icon={<DollarOutlined />}>
          {formatAmount(amount)} SCAI
        </Tag>
      ),
      sorter: (a, b) => a.scai_amount - b.scai_amount,
      width: 150,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? 'Active' : 'Completed'}
        </Tag>
      ),
      width: 100,
    },
  ];

  if (!isAuthenticated) {
    return (
      <Card>
        <Empty
          description="Please login to access the bounty system"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div className="bounty-system">
      {/* 统计信息 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Active Bounties"
                value={statistics.active_bounties?.count || 0}
                prefix={<TrophyOutlined />}
                suffix="bounties"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Rewards"
                value={statistics.active_bounties?.total_amount || 0}
                prefix={<DollarOutlined />}
                suffix="SCAI"
                formatter={(value) => formatAmount(value)}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Completed"
                value={statistics.completed_bounties?.count || 0}
                prefix={<CheckCircleOutlined />}
                suffix="bounties"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 主要内容 */}
      <Tabs defaultActiveKey="active">
        <TabPane tab="Active Bounties" key="active">
          <Card
            title="Active Bounties"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadBounties}>
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Create Bounty
                </Button>
              </Space>
            }
          >
            <Table
              columns={bountyColumns}
              dataSource={bounties}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              scroll={{ x: 800 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="My Bounties" key="my">
          <Row gutter={16}>
            <Col span={12}>
              <Card title="Created by Me" size="small">
                <Table
                  columns={bountyColumns.slice(0, 3)}
                  dataSource={userBounties.created}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Completed by Me" size="small">
                <Table
                  columns={bountyColumns.slice(0, 3)}
                  dataSource={userBounties.completed}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* 创建悬赏模态框 */}
      <Modal
        title="Create New Bounty"
        open={createModalVisible}
        onOk={handleCreateBounty}
        onCancel={() => setCreateModalVisible(false)}
        confirmLoading={createLoading}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>DOI *</Text>
            <Input
              placeholder="e.g., 10.1000/example.2025"
              value={createForm.doi}
              onChange={(e) => setCreateForm({ ...createForm, doi: e.target.value })}
            />
          </div>

          <div>
            <Text strong>Bounty Amount (SCAI) *</Text>
            <InputNumber
              min={1000}
              max={10000}
              step={100}
              value={createForm.amount}
              onChange={(value) => setCreateForm({ ...createForm, amount: value })}
              style={{ width: '100%' }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
            <Text type="secondary">Minimum: 1,000 SCAI, Maximum: 10,000 SCAI</Text>
          </div>

          <div>
            <Text strong>Title</Text>
            <Input
              placeholder="Brief description of the paper needed"
              value={createForm.title}
              onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
            />
          </div>

          <div>
            <Text strong>Description</Text>
            <TextArea
              rows={3}
              placeholder="Additional details about the bounty"
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />
          </div>

          <div>
            <Text strong>Solana Transaction ID *</Text>
            <Input
              placeholder="Transaction ID of SCAI token transfer"
              value={createForm.txId}
              onChange={(e) => setCreateForm({ ...createForm, txId: e.target.value })}
            />
            <Text type="secondary">
              Transfer {formatAmount(createForm.amount)} SCAI to the bounty wallet first
            </Text>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default BountySystem;
